import { API_URL } from '../config/api';
import { AUTH_ENDPOINTS, LoginRequest, RegisterRequest, EmailConfirmationRequest, ForgotPasswordRequest, ResetPasswordRequest, AuthResponse } from '../config/auth';
import { httpClient } from './httpClient';

export interface UserProfile {
  id: string;
  nickname: string;
  email: string;
}

const api = {
  post: async (endpoint: string, data: any) => {
    return httpClient.post(endpoint, data);
  },

  get: async (endpoint: string) => {
    return httpClient.get(endpoint);
  }
};

export const authService = {
  login: async (identifier: string, password: string): Promise<AuthResponse> => {
    const response = await api.post(AUTH_ENDPOINTS.login, { Identifier: identifier, Password: password });
    
    if (response.success && response.token) {
      localStorage.setItem('auth_token', response.token);
    }
    
    return response;
  },

  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    try {
      const response = await api.post(AUTH_ENDPOINTS.register, data);
      
      if (!response.success) {
        return {
          success: false, 
          error: response.error || 'Ошибка при регистрации',
          token: response.token,
          userId: response.userId,
          email: response.email,
          emailConfirmed: response.emailConfirmed,
          requiresEmailConfirmation: response.requiresEmailConfirmation
        };
      }
      
      return response;
    } catch (error: any) {
      if (error.response?.data?.message) {
        return {
          success: false,
          error: error.response.data.message
        };
      }
      
      return {
        success: false,
        error: error.message || 'Ошибка при регистрации'
      };
    }
  },

  logout: async (): Promise<void> => {
    try {
      await api.post(AUTH_ENDPOINTS.logout, {});
    } finally {
      localStorage.removeItem('auth_token');
    }
  },

  confirmEmail: async (data: EmailConfirmationRequest): Promise<AuthResponse> => {
    const response = await api.post(AUTH_ENDPOINTS.confirmEmail, data);
    return response;
  },

  forgotPassword: async (email: string): Promise<AuthResponse> => {
    try {
      const response = await api.post(AUTH_ENDPOINTS.forgotPassword, { email });
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Ошибка при отправке запроса на восстановление пароля'
      };
    }
  },

  resetPassword: async (data: ResetPasswordRequest): Promise<AuthResponse> => {
    try {
      const response = await api.post(AUTH_ENDPOINTS.resetPassword, data);
      return response;
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Ошибка при сбросе пароля'
      };
    }
  },

  googleAuth: async (accessToken: string): Promise<AuthResponse> => {
    try {
      const response = await api.post('/auth/google', { accessToken });
      
      if (response.token) {
        localStorage.setItem('auth_token', response.token);
      }
      
      return response;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('auth_token');
  },

  getToken: (): string | null => {
    return localStorage.getItem('auth_token');
  },

  getProfile: async (): Promise<UserProfile> => {
    try {
      const response = await api.get(AUTH_ENDPOINTS.profile);
      return {
        id: response.id || '',
        nickname: response.nickname || '',
        email: response.email || ''
      };
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  },

  setNickname: async (nickname: string, token: string): Promise<void> => {
    try {
      await api.post(AUTH_ENDPOINTS.nickname, { nickname });
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw error;
    }
  }
};